// Attendance table columns — the person (avatar + name), the punch-state chip,
// the relative last-seen time, the check-in/check-out punches (photo thumb +
// clock time; check-out dashes until the person clocks out), and the shift's
// total hours.
//
// NO column carries `sortable`: the confirmed list body is { page, limit } with
// no orderBy, and DataTable draws a glyph for any sortable column — a header
// that cannot sort is a dead affordance. The design's chevrons return when the
// contract grows sort keys.
//
// Punch photos: the page batch-resolves the rows' imageNames to download URLs
// (POST media/download-clock-in-media) and passes the map in; a cell whose
// photo is absent — or whose URL hasn't arrived yet — renders an image-off
// icon box instead.

import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Icon, type ChipVariant, type DataTableColumn } from '@/components/common';
import { ClockInState, type AttendanceRecord } from '@/api/endpoints/attendanceApi';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';
import i18n from '@/i18n/config';
import { fmtAgo, fmtClock } from './utils/lastSeen';

/** Punch state → chip tone + label. The wire enum is confirmed, so this is
 *  exhaustive — no fallback branch. */
export const STATE_META: Record<ClockInState, { chip: ChipVariant; labelKey: string }> = {
  [ClockInState.Started]: { chip: 'success', labelKey: 'attendance_status_working' },
  [ClockInState.Paused]: { chip: 'warning', labelKey: 'attendance_status_paused' },
  [ClockInState.Ended]: { chip: 'neutral', labelKey: 'attendance_status_out' },
};

const dash = <span className='attendance-table__dash'>—</span>;

/** Seconds → "8h 22m" through the i18n key so Arabic gets its own unit glyphs. */
const fmtDuration = (
  secs: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
  const mins = Math.floor(secs / 60);
  return t('attendance_duration', { h: Math.floor(mins / 60), m: mins % 60 });
};

/** A punch cell: the photo thumb (real when its URL has resolved, an
 *  image-off box otherwise) + the punch's clock time. The photo is decorative —
 *  the time sits beside it and the person's name leads the row. */
const punch = (iso: string, url?: string) => (
  <span className='attendance-table__punch'>
    {url ? (
      <img className='attendance-table__photo attendance-table__photo--img' src={url} alt='' />
    ) : (
      <span className='attendance-table__photo attendance-table__photo--empty' aria-hidden='true'>
        <Icon name='image-off' size={14} />
      </span>
    )}
    <span className='attendance-table__punch-time'>{fmtClock(iso)}</span>
  </span>
);

/** The shift's seconds — live (now − clockIn) while the person is still on
 *  shift (started/paused), the wire total once ended: an open shift's
 *  totalClockTimeSecs reads 0 until checkout, which would be a lie on screen. */
export const shiftSecondsOf = (r: AttendanceRecord): number =>
  r.state === ClockInState.Ended
    ? r.totalClockTimeSecs
    : Math.max(0, Math.floor((Date.now() - new Date(r.clockInDate).getTime()) / 1000));

/** "8h 22m" of a record's shift, live while open (see shiftSecondsOf). */
export const fmtShiftDuration = (
  r: AttendanceRecord,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string => fmtDuration(shiftSecondsOf(r), t);

export const useAttendanceColumns = (
  /** imageName → downloadUrl, batch-resolved by the page. */
  mediaUrls: Record<string, string>,
): DataTableColumn<AttendanceRecord>[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'name',
      label: t('attendance_col_name'),
      width: 'minmax(14rem, 1.6fr)',
      render: (r) => (
        <span className='attendance-table__person'>
          <Avatar initials={initialsOf(r.fullName)} size={36} tone={toneOf(r.fullName)} />
          <span className='attendance-table__person-name'>{r.fullName}</span>
        </span>
      ),
    },
    {
      key: 'status',
      label: t('attendance_col_status'),
      width: 'minmax(9rem, 1fr)',
      render: (r) => {
        const meta = STATE_META[r.state];
        return (
          <Chip variant={meta.chip} dot>
            {t(meta.labelKey)}
          </Chip>
        );
      },
    },
    {
      key: 'lastSeen',
      label: t('attendance_col_last_seen'),
      width: 'minmax(8rem, 1fr)',
      render: (r) => <span className='attendance-table__ago'>{fmtAgo(r.updatedAt, t)}</span>,
    },
    {
      key: 'checkIn',
      label: t('attendance_col_check_in'),
      width: 'minmax(8.5rem, 1fr)',
      render: (r) => punch(r.clockInDate, r.clockInPhoto && mediaUrls[r.clockInPhoto.imageName]),
    },
    {
      key: 'checkOut',
      label: t('attendance_col_check_out'),
      width: 'minmax(8.5rem, 1fr)',
      render: (r) =>
        r.clockOutDate
          ? punch(r.clockOutDate, r.clockOutPhoto && mediaUrls[r.clockOutPhoto.imageName])
          : dash,
    },
    {
      key: 'totalHours',
      label: t('attendance_col_total_hours'),
      width: 'minmax(7rem, 0.8fr)',
      render: (r) => <span className='attendance-table__hours'>{fmtShiftDuration(r, t)}</span>,
    },
  ];
};

/** The detail drawer's Timesheet columns — one row per shift of ONE person:
 *  date + weekday, the two punches (photo thumb + time; check-out dashes while
 *  on shift) and the shift total (live while open). */
export const useTimesheetColumns = (
  /** imageName → downloadUrl, batch-resolved by the drawer per page. */
  mediaUrls: Record<string, string>,
): DataTableColumn<AttendanceRecord>[] => {
  const { t } = useTranslation();

  const day = (iso: string) => {
    const d = new Date(iso);
    return {
      date: new Intl.DateTimeFormat(i18n.language, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }).format(d),
      weekday: new Intl.DateTimeFormat(i18n.language, { weekday: 'long' }).format(d),
    };
  };

  return [
    {
      key: 'date',
      label: t('attendance_drawer_col_date'),
      width: 'minmax(10rem, 1.2fr)',
      render: (r) => {
        const { date, weekday } = day(r.clockInDate);
        return (
          <span className='attendance-timesheet__day'>
            <span className='attendance-timesheet__day-date'>{date}</span>
            <span className='attendance-timesheet__day-name'>{weekday}</span>
          </span>
        );
      },
    },
    {
      key: 'checkIn',
      label: t('attendance_col_check_in'),
      width: 'minmax(8.5rem, 1fr)',
      render: (r) => punch(r.clockInDate, r.clockInPhoto && mediaUrls[r.clockInPhoto.imageName]),
    },
    {
      key: 'checkOut',
      label: t('attendance_col_check_out'),
      width: 'minmax(8.5rem, 1fr)',
      render: (r) =>
        r.clockOutDate
          ? punch(r.clockOutDate, r.clockOutPhoto && mediaUrls[r.clockOutPhoto.imageName])
          : dash,
    },
    {
      key: 'totalHours',
      label: t('attendance_col_total_hours'),
      width: 'minmax(7rem, 0.8fr)',
      render: (r) => <span className='attendance-table__hours'>{fmtShiftDuration(r, t)}</span>,
    },
  ];
};
